<template>
  <div v-if="visible" class="modal-backdrop">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Configure G703 Template</h2>
        <button @click="$emit('close')" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-content">
        <div class="template-info">
          <p>Configure how to extract and categorize data from your G703 documents. This template will be applied to all G703 files in this project.</p>
          
          <div class="file-info">
            <p><strong>Sample File:</strong> {{ sampleFileName }}</p>
            <p><strong>Project:</strong> {{ projectName }}</p>
            <p><strong>Draw Number:</strong> {{ actualDrawNumber || 'Not specified' }}</p>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-section">
          <div class="spinner"></div>
          <p>Loading G703 data...</p>
        </div>

        <!-- G703 Configuration Flow -->
        <div v-if="!isLoading && g703Data" class="g703-config-section">
          <!-- Current Step Display -->
          <div class="current-step">
            <h4>{{ currentStepTitle }}</h4>
            <p>{{ currentStepDescription }}</p>
          </div>

          <!-- Step 1: G703 Column Mapping -->
          <div v-if="currentStep === 'column_mapping'" class="g703-column-mapping-step">
            <div class="mapping-header">
              <h4>G703 Column Mapping</h4>
              <p>First, map each column to the correct G703 field. Click on the columns below to assign them:</p>
              
              <!-- EXACT copy of mapping buttons from ProjectImportG703Mapper -->
              <div class="g703-mapping-buttons-grid">
                <div class="mapping-section">
                  <h6>Basic Information</h6>
                  <button 
                    :class="{ active: currentMappingField === 'trade_code_column' }"
                    @click="currentMappingField = 'trade_code_column'"
                    class="mapping-btn trade-code-btn">
                    🏷️ Trade Code
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'description_column' }"
                    @click="currentMappingField = 'description_column'"
                    class="mapping-btn description-btn">
                    📝 Description of Work
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'contractor_names_column' }"
                    @click="currentMappingField = 'contractor_names_column'"
                    class="mapping-btn contractor-btn">
                    👷 Names (Contractor/Sub)
                  </button>
                </div>
                
                <div class="mapping-section">
                  <h6>Budget Values</h6>
                  <button 
                    :class="{ active: currentMappingField === 'scheduled_value_column' }"
                    @click="currentMappingField = 'scheduled_value_column'"
                    class="mapping-btn scheduled-value-btn">
                    💰 Scheduled Value
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'owner_previous_approved_column' }"
                    @click="currentMappingField = 'owner_previous_approved_column'"
                    class="mapping-btn owner-previous-btn">
                    ✅ Owner Previous Approved
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'adjusted_schedule_values_column' }"
                    @click="currentMappingField = 'adjusted_schedule_values_column'"
                    class="mapping-btn adjusted-schedule-btn">
                    📊 Adjusted Schedule Values
                  </button>
                </div>
                
                <div class="mapping-section">
                  <h6>Work Completed</h6>
                  <button 
                    :class="{ active: currentMappingField === 'work_completed_previous_column' }"
                    @click="currentMappingField = 'work_completed_previous_column'"
                    class="mapping-btn previous-work-btn">
                    ⏮️ Work Completed Previous
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'work_completed_this_period_column' }"
                    @click="currentMappingField = 'work_completed_this_period_column'"
                    class="mapping-btn current-work-btn">
                    🔄 Work Completed This Period
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'materials_stored_column' }"
                    @click="currentMappingField = 'materials_stored_column'"
                    class="mapping-btn materials-btn">
                    📦 Materials Presently Stored
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'total_completed_column' }"
                    @click="currentMappingField = 'total_completed_column'"
                    class="mapping-btn total-completed-btn">
                    📋 Total Completed and Stored
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'percent_complete_column' }"
                    @click="currentMappingField = 'percent_complete_column'"
                    class="mapping-btn percent-complete-btn">
                    📈 Percentage Complete
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'balance_to_finish_column' }"
                    @click="currentMappingField = 'balance_to_finish_column'"
                    class="mapping-btn balance-finish-btn">
                    🎯 Balance to Finish
                  </button>
                </div>
                
                <div class="mapping-section">
                  <h6>Retainage</h6>
                  <button 
                    :class="{ active: currentMappingField === 'retainage_percent_column' }"
                    @click="currentMappingField = 'retainage_percent_column'"
                    class="mapping-btn retainage-percent-btn">
                    🔒 Retainage %
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'retainage_this_period_column' }"
                    @click="currentMappingField = 'retainage_this_period_column'"
                    class="mapping-btn retainage-period-btn">
                    🔒 Retainage This Period
                  </button>
                  <button 
                    :class="{ active: currentMappingField === 'retainage_total_column' }"
                    @click="currentMappingField = 'retainage_total_column'"
                    class="mapping-btn retainage-total-btn">
                    🔒 Retainage Total
                  </button>
                </div>
              </div>
              
              <p class="mapping-instruction">
                <strong>Currently mapping:</strong> {{ g703MappingLabels[currentMappingField] || 'Description of Work' }}
                <span v-if="g703ColumnMapping[currentMappingField] !== null" class="current-mapping">
                  (Mapped to Column {{ g703ColumnMapping[currentMappingField] + 1 }})
                </span>
              </p>
            </div>
            
            <!-- G703 Table Preview -->
            <div class="g703-table-preview">
              <h5>Click on the Header Row:</h5>
              <p class="table-info">Showing {{ sampleG703Rows?.[0]?.length || 0 }} extracted data points from your G703 document</p>
              <div class="raw-table-container">
                <table class="raw-g703-table mapping-mode">
                  <tbody>
                    <tr v-for="(row, rowIndex) in sampleG703Rows" :key="rowIndex"
                        :class="{ 'selected-header-row': g703HeaderRow === rowIndex }"
                        @click="selectG703HeaderRow(rowIndex)">
                      <td class="row-number">{{ rowIndex + 1 }}</td>
                      <td v-for="(cell, cellIndex) in (Array.isArray(row) ? row : [row])" :key="cellIndex" 
                          class="table-cell clickable-cell"
                          :class="getG703ColumnHighlight(cellIndex, currentMappingField)"
                          @click.stop="selectG703Column(cellIndex, currentMappingField)">
                        {{ getCellDisplayText(cell) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <!-- Mapping Summary -->
            <div class="mapping-summary">
              <h5>Column Mappings:</h5>
              <div class="mapping-list">
                <div v-for="(label, mappingKey) in g703MappingLabels" :key="mappingKey" class="mapping-summary-item">
                  <span class="field-label">{{ label }}:</span>
                  <span class="mapped-column" :class="{ mapped: g703ColumnMapping[mappingKey] !== null }">
                    {{ g703ColumnMapping[mappingKey] !== null ? `Column ${g703ColumnMapping[mappingKey] + 1}` : 'Not mapped' }}
                  </span>
                </div>
              </div>
              
              <div class="mapping-progress-section">
                <p><strong>Mapping Progress:</strong> {{ mappedColumnsCount }}/17 columns mapped ({{ essentialMappedCount }}/4 essential)</p>
              </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="action-buttons">
              <button @click="proceedToDataReview" :disabled="!canProceedToReview" class="btn btn-primary">
                Apply Column Mapping & Continue
              </button>
            </div>
          </div>

          <!-- Step 2: Data Review -->
          <div v-if="currentStep === 'data_review'" class="g703-data-review-step">
            <h4>Review Processed G703 Data</h4>
            <p>Review the processed G703 data and make any necessary adjustments:</p>
            
            <div class="processed-data-table">
              <table class="g703-review-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Original Budget</th>
                    <th>Current Budget</th>
                    <th>Previously Funded</th>
                    <th>Current Request</th>
                    <th>Total Completed</th>
                    <th>Materials Stored</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in processedG703Data" :key="index">
                    <td>{{ item.description }}</td>
                    <td>{{ formatAmount(item.original_budget) }}</td>
                    <td>{{ formatAmount(item.current_budget) }}</td>
                    <td>{{ formatAmount(item.previously_funded) }}</td>
                    <td>{{ formatAmount(item.current_request) }}</td>
                    <td>{{ formatAmount(item.total_completed_to_date) }}</td>
                    <td>{{ formatAmount(item.materials_presently_stored) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="action-buttons">
              <button @click="currentStep = 'column_mapping'" class="btn btn-secondary">
                Back to Column Mapping
              </button>
              <button @click="saveTemplate" class="btn btn-primary">
                Save G703 Template
              </button>
            </div>
          </div>
        </div>

        <!-- No Data State -->
        <div v-if="!isLoading && !g703Data" class="no-data-section">
          <p>⚠️ Unable to load G703 preview data</p>
          <p>The template will be created with default settings.</p>
          
          <div class="action-buttons">
            <button @click="$emit('close')" class="btn btn-secondary">Cancel</button>
            <button @click="saveTemplate" class="btn btn-primary">Save G703 Template</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
  visible: Boolean,
  projectId: [String, Number],
  projectName: String,
  documentId: [String, Number],
  sampleFileName: String,
  drawNumber: [String, Number]
})

const emit = defineEmits(['close', 'template-saved'])

// State variables - EXACT copy from ProjectImportG703Mapper
const isLoading = ref(true)
const g703Data = ref(null)
const currentStep = ref('column_mapping')
const currentMappingField = ref('description_column')
const g703HeaderRow = ref(0)
const g703ColumnMapping = ref({
  trade_code_column: null,
  description_column: null,
  contractor_names_column: null,
  scheduled_value_column: null,
  budget_transfer_column: null,
  owner_previous_approved_column: null,
  owner_approved_completed_column: null,
  adjusted_schedule_values_column: null,
  subcontractor_completed_column: null,
  work_completed_previous_column: null,
  work_completed_this_period_column: null,
  materials_stored_column: null,
  total_completed_column: null,
  percent_complete_column: null,
  balance_to_finish_column: null,
  retainage_percent_column: null,
  retainage_this_period_column: null,
  retainage_total_column: null
})
const processedG703Data = ref([])
const sampleG703Rows = ref([])

// G703 mapping field labels - EXACT match to ProjectImportG703Mapper
const g703MappingLabels = {
  'trade_code_column': 'Trade Code',
  'description_column': 'Description of Work',
  'contractor_names_column': 'Names (Contractor/Sub)',
  'scheduled_value_column': 'Scheduled Value',
  'budget_transfer_column': 'Budget Transfer',
  'owner_previous_approved_column': 'Owner Previous Approved',
  'owner_approved_completed_column': 'Owner Approved Completed',
  'adjusted_schedule_values_column': 'Adjusted Schedule Values',
  'subcontractor_completed_column': 'Subcontractor Completed',
  'work_completed_previous_column': 'Work Completed Previous',
  'work_completed_this_period_column': 'Work Completed This Period',
  'materials_stored_column': 'Materials Presently Stored',
  'total_completed_column': 'Total Completed and Stored',
  'percent_complete_column': 'Percentage Complete',
  'balance_to_finish_column': 'Balance to Finish',
  'retainage_percent_column': 'Retainage %',
  'retainage_this_period_column': 'Retainage This Period',
  'retainage_total_column': 'Retainage Total'
}

// Computed properties - EXACT copy from ProjectImportG703Mapper
const currentStepTitle = computed(() => {
  switch (currentStep.value) {
    case 'column_mapping': return 'Step 1: Column Mapping'
    case 'data_review': return 'Step 2: Data Review'
    default: return 'G703 Configuration'
  }
})

const currentStepDescription = computed(() => {
  switch (currentStep.value) {
    case 'column_mapping': return 'Map the extracted columns to G703 fields'
    case 'data_review': return 'Review the processed data before saving'
    default: return 'Configure your G703 template'
  }
})

const mappedColumnsCount = computed(() => {
  return Object.values(g703ColumnMapping.value).filter(val => val !== null).length
})

const essentialMappedCount = computed(() => {
  const essentialFields = ['description_column', 'scheduled_value_column', 'work_completed_this_period_column', 'total_completed_column']
  return essentialFields.filter(field => g703ColumnMapping.value[field] !== null).length
})

const canProceedToReview = computed(() => {
  return essentialMappedCount.value >= 2 // At least 2 essential fields mapped
})

// Methods - EXACT copy from ProjectImportG703Mapper with DrawTemplate endpoints
const loadG703Preview = async () => {
  if (!props.projectId || !props.documentId) return
  
  isLoading.value = true
  try {
    // Use documentId directly as DrawTemplate ID (not document_id lookup)
    const drawTemplateId = props.documentId
    console.log('✅ Using DrawTemplate ID directly:', drawTemplateId)
    
    // Use the correct DrawTemplate g703_data endpoint
    const response = await fetch(`/api/v1/draw_templates/${drawTemplateId}/g703_data`)
    const data = await response.json()
    
    if (data.success && data.data) {
      g703Data.value = data
      sampleG703Rows.value = data.data.raw_table_data || []
      console.log(`✅ G703 data loaded: ${sampleG703Rows.value.length} rows`)
    } else {
      console.warn('No G703 data available:', data.error)
    }
  } catch (error) {
    console.error('Error loading G703 preview:', error)
  } finally {
    isLoading.value = false
  }
}

const selectG703HeaderRow = (rowIndex) => {
  g703HeaderRow.value = rowIndex
  console.log(`Selected header row: ${rowIndex}`)
}

const selectG703Column = (columnIndex, mappingField) => {
  console.log(`Mapped column ${columnIndex} to ${mappingField}`)
  g703ColumnMapping.value[mappingField] = columnIndex
}

const getG703ColumnHighlight = (columnIndex, currentField) => {
  const mappedField = Object.keys(g703ColumnMapping.value).find(
    key => g703ColumnMapping.value[key] === columnIndex
  )
  
  if (mappedField === currentField) {
    return 'current-mapping-highlight'
  } else if (mappedField) {
    return 'mapped-column-highlight'
  }
  return ''
}

const getCellDisplayText = (cell) => {
  if (cell === null || cell === undefined) return ''
  return String(cell).substring(0, 50) + (String(cell).length > 50 ? '...' : '')
}

const proceedToDataReview = async () => {
  console.log('=== APPLYING COLUMN MAPPING FOR RE-PROCESSING ===')
  console.log('📋 Project ID:', props.projectId)
  console.log('📋 Document ID (DrawTemplate ID):', props.documentId) 
  console.log('📋 Draw Number:', props.drawNumber)
  
  isLoading.value = true
  
  try {
    // Use documentId directly as DrawTemplate ID (not document_id lookup)
    const drawTemplateId = props.documentId
    console.log('✅ Using DrawTemplate ID directly:', drawTemplateId)
    
    // Send column mappings to backend for re-processing using NEW apply_g703_template endpoint (like project uploader)
    const mappingPayload = {
      template: {
        template_type: 'g703',
        project_id: props.projectId,
        draw_number: props.drawNumber,
        column_mappings: {
          g703_column_mapping: g703ColumnMapping.value,
          header_row_index: g703HeaderRow.value || 0
        }
      }
    }
    
    console.log('Sending column mappings to backend:', mappingPayload)
    
    const response = await fetch(`/api/v1/draw_templates/${drawTemplateId}/apply_g703_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mappingPayload)
    })
    
    const data = await response.json()
    
    if (data.success) {
      console.log('✅ Column mapping applied successfully!')
      console.log(`📋 Received ${data.structured_data?.length || 0} structured G703 records for review`)
      
      // Get the processed data for review (like project uploader)
      processedG703Data.value = data.structured_data || []
      currentStep.value = 'data_review'
    } else {
      throw new Error(data.error || 'Failed to apply column mapping')
    }
  } catch (error) {
    console.error('❌ Error applying template:', error)
    alert(`Error applying template: ${error.message}`)
  } finally {
    isLoading.value = false
  }
}

const saveTemplate = async () => {
  console.log('=== FINALIZING G703 TEMPLATE ===')
  
  isLoading.value = true
  
  try {
    const drawTemplateId = props.documentId
    
    // Finalize the template with column mappings (saves it permanently)
    const finalizePayload = {
      template: {
        template_type: 'g703',
        status: 'active',
        project_id: props.projectId,
        draw_number: props.drawNumber,
        column_mappings: {
          g703_column_mapping: g703ColumnMapping.value,
          header_row_index: g703HeaderRow.value || 0
        }
      }
    }
    
    console.log('Finalizing template with payload:', finalizePayload)
    
    const response = await fetch(`/api/v1/draw_templates/${drawTemplateId}/finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalizePayload)
    })
    
    const data = await response.json()
    
    if (data.success) {
      console.log('✅ G703 template finalized successfully!')
      emit('template-saved', { success: true })
      emit('close')
    } else {
      throw new Error(data.error || 'Failed to finalize template')
    }
  } catch (error) {
    console.error('Save template error:', error)
    alert(`Error saving template: ${error.message}`)
  } finally {
    isLoading.value = false
  }
}

const formatAmount = (amount) => {
  if (!amount || amount === 0) return '$0.00'
  return `$${parseFloat(amount).toLocaleString()}`
}

// Watch for modal visibility changes
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    loadG703Preview()
  }
})

// Initialize when component mounts
onMounted(() => {
  if (props.visible) {
    loadG703Preview()
  }
})

// New computed property to get actual draw number from loaded G703 data
const actualDrawNumber = computed(() => {
  if (g703Data.value && g703Data.value.data && g703Data.value.data.draw_number) {
    return g703Data.value.data.draw_number
  }
  return null
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 1400px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #4A5FD5;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.close-btn:hover {
  color: #333;
}

.modal-content {
  padding: 20px;
}

.template-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.file-info {
  background: #e3f2fd;
  padding: 12px;
  border-radius: 6px;
  margin-top: 12px;
}

.loading-section {
  text-align: center;
  padding: 40px 0;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Professional G703 Column Mapping Styles - EXACT copy from working ProjectImportG703Mapper */
.g703-config-section {
  margin-bottom: 30px;
}

.current-step {
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.g703-column-mapping-step {
  margin-bottom: 30px;
}

.mapping-header h4 {
  color: #333;
  margin-bottom: 8px;
}

.mapping-header p {
  color: #666;
  margin-bottom: 20px;
}

.g703-mapping-buttons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.mapping-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.mapping-section h6 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mapping-section .mapping-btn {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  text-align: left;
}

.mapping-btn {
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.mapping-btn:hover {
  border-color: #2196f3;
  background: #f0f8ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.mapping-btn.active {
  border-color: #2196f3;
  background: #2196f3;
  color: white;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.mapping-instruction {
  background: #e3f2fd;
  padding: 12px;
  border-radius: 6px;
  margin: 16px 0;
  border: 1px solid #90caf9;
}

.current-mapping {
  color: #2196f3;
  font-weight: 600;
}

/* G703 Table Preview Styles - EXACT copy from working component */
.g703-table-preview {
  margin-top: 20px;
}

.g703-table-preview h5 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
}

.table-info {
  margin: 0 0 15px 0;
  color: #666;
  font-size: 14px;
}

.raw-table-container {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 20px;
}

.raw-g703-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
  min-width: 600px;
}

.raw-g703-table.mapping-mode tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.raw-g703-table.mapping-mode tr:hover {
  background-color: #f8f9fa;
}

.raw-g703-table.mapping-mode tr.selected-header-row {
  background-color: #e3f2fd !important;
  border: 2px solid #2196F3;
  font-weight: 600;
}

.raw-g703-table .row-number {
  background-color: #f5f5f5;
  padding: 4px 6px;
  border: 1px solid #ddd;
  font-weight: 600;
  width: 40px;
  text-align: center;
  position: sticky;
  left: 0;
  z-index: 2;
  font-size: 10px;
}

.raw-g703-table .table-cell {
  padding: 4px 6px;
  border: 1px solid #ddd;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  font-size: 10px;
}

.raw-g703-table .table-cell.clickable-cell {
  cursor: pointer;
  transition: background-color 0.2s;
}

.raw-g703-table .table-cell.clickable-cell:hover {
  background-color: #f0f8ff;
}

/* Column highlighting - Professional colors */
.raw-g703-table .table-cell.highlight-description {
  background-color: #e3f2fd !important;
  color: #1565c0 !important;
  border: 2px solid #2196f3 !important;
  font-weight: 600;
}

.raw-g703-table .table-cell.highlight-scheduled_value {
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
  border: 2px solid #4caf50 !important;
  font-weight: 600;
}

.raw-g703-table .table-cell.highlight-work_completed_this_period {
  background-color: #f3e5f5 !important;
  color: #6a1b9a !important;
  border: 2px solid #9c27b0 !important;
  font-weight: 600;
}

.raw-g703-table .table-cell.highlight-total_completed {
  background-color: #fff3e0 !important;
  color: #e65100 !important;
  border: 2px solid #ff9800 !important;
  font-weight: 600;
}

.raw-g703-table .table-cell.current-mapping-highlight {
  background: #2196f3 !important;
  color: white !important;
  font-weight: 600;
  border: 2px solid #1976d2 !important;
}

.raw-g703-table .table-cell.mapped-column-highlight {
  background: #f6ffed !important;
  color: #52c41a !important;
  border: 2px solid #52c41a !important;
}

/* EXACT mapping summary styles from working component */
.mapping-summary {
  margin-top: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
}

.mapping-summary h5 {
  color: #333;
  margin-bottom: 15px;
}

.mapping-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.mapping-summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  font-size: 12px;
}

.field-label {
  font-weight: 600;
  color: #333;
}

.mapped-column {
  color: #999;
}

.mapped-column.mapped {
  color: #4caf50;
  font-weight: 600;
}

.mapping-progress-section {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-top: 15px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  justify-content: flex-end;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-primary {
  background: #2196f3;
  color: white;
}

.btn-primary:hover {
  background: #1976d2;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #d9d9d9;
}

.btn-secondary:hover {
  background: #e6f7ff;
  border-color: #2196f3;
  transform: translateY(-1px);
}

.no-data-section {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.g703-data-review-step {
  margin-top: 20px;
}

.processed-data-table {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin: 20px 0;
}

.g703-review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.g703-review-table th {
  background: #fafafa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #d9d9d9;
  position: sticky;
  top: 0;
}

.g703-review-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}
</style> 